import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Brain, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(data.detail || "Check your inbox");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-appbg text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="forgot-password-page">
        <Link to="/" className="flex items-center gap-2 mb-8" data-testid="forgot-logo-link">
          <Brain className="text-blaze" size={22} />
          <span className="heading text-2xl">CoachConnect <span className="text-blaze">AI</span></span>
        </Link>

        {sent ? (
          <div className="border border-neutral-800 bg-appcard p-8" data-testid="forgot-sent-card">
            <div className="flex items-center gap-2 text-volt text-xs tracking-widest">
              <CheckCircle2 size={14} /> RESET LINK SENT
            </div>
            <h1 className="heading text-4xl mt-2">CHECK YOUR EMAIL</h1>
            <p className="text-neutral-400 text-sm mt-3">
              If an account exists for <span className="text-white">{email}</span>, we&apos;ve sent a password-reset link.
              It expires in <span className="text-white">15 minutes</span> and can only be used once.
            </p>
            <p className="text-neutral-500 text-xs mt-4">
              Didn&apos;t get it? Check spam, or try again with a different email.
            </p>
            <Link
              to="/auth"
              data-testid="forgot-back-to-login"
              className="mt-6 w-full min-h-[48px] border border-neutral-800 text-neutral-300 text-sm hover:border-volt hover:text-volt transition-all flex items-center justify-center gap-2"
            >
              <ArrowLeft size={14} /> Back to login
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-neutral-800 bg-appcard p-8 space-y-4" data-testid="forgot-form">
            <div className="text-xs tracking-widest text-blaze">FORGOT PASSWORD</div>
            <h1 className="heading text-4xl">RESET IT</h1>
            <p className="text-neutral-400 text-sm">
              Enter the email tied to your CoachConnect AI account. We&apos;ll send you a link to pick a new password.
            </p>
            <div>
              <div className="text-[11px] tracking-widest text-neutral-500 mb-1.5">EMAIL</div>
              <input
                data-testid="forgot-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-appcard border border-neutral-800 px-3.5 py-3 text-sm outline-none focus:border-blaze min-h-[44px]"
                placeholder="you@example.com"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="forgot-submit-btn"
              className="w-full min-h-[48px] bg-blaze text-white heading text-lg tracking-wider hover:glow-blaze transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Mail size={16} /> {busy ? "SENDING…" : "SEND RESET LINK"}
            </button>
            <Link
              to="/auth"
              data-testid="forgot-cancel-link"
              className="block text-center text-neutral-500 text-xs hover:text-volt transition-colors"
            >
              Remembered it? Back to login
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
